import type { Dispatch, SetStateAction } from 'react'
import type { NumericFacet, NumericFilterState, OptionFacet, OptionFilterState } from './types'
import { SearchNumericFacetSection, SearchOptionFacetSection } from './SearchFiltersFacetSections'
import { formatProductTypeLabel } from './productFilters.shared'
import PriceRangeSlider from './PriceRangeSlider'

type Props = {
  language: 'it' | 'en'
  activeFilterCount: number
  priceBounds: { min: number; max: number } | null
  priceRange: { min: string; max: string }
  setPriceRange: Dispatch<SetStateAction<{ min: string; max: string }>>
  brandValues: string[]
  selectedBrands: string[]
  setSelectedBrands: Dispatch<SetStateAction<string[]>>
  productTypeValues: string[]
  selectedTypes: string[]
  setSelectedTypes: Dispatch<SetStateAction<string[]>>
  numericFacets: NumericFacet[]
  numericFilters: NumericFilterState
  setNumericFilters: Dispatch<SetStateAction<NumericFilterState>>
  optionFacets: OptionFacet[]
  optionFilters: OptionFilterState
  setOptionFilters: Dispatch<SetStateAction<OptionFilterState>>
  onResetFilters: () => void
}

const SearchFiltersSidebar = ({
  language,
  activeFilterCount,
  priceBounds,
  priceRange,
  setPriceRange,
  brandValues,
  selectedBrands,
  setSelectedBrands,
  productTypeValues,
  selectedTypes,
  setSelectedTypes,
  numericFacets,
  numericFilters,
  setNumericFilters,
  optionFacets,
  optionFilters,
  setOptionFilters,
  onResetFilters,
}: Props) => (
  <div className="h-fit border-y border-slate-200 py-3">
    <div className="mb-3 flex items-center justify-between border-b border-slate-200 pb-3">
      <p className="text-sm font-bold uppercase tracking-[0.08em] text-slate-900">{language === 'en' ? 'Filters' : 'Filtri'}</p>
      <button
        type="button"
        onClick={onResetFilters}
        className="text-xs font-semibold text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline"
      >
        {language === 'en' ? 'Reset' : 'Reset'}
      </button>
    </div>
    <p className="mb-1 text-xs text-slate-500">
      {language === 'en' ? `${activeFilterCount} active filters` : `${activeFilterCount} filtri attivi`}
    </p>

    {priceBounds && (
      <section className="border-b border-slate-200 py-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
          {language === 'en' ? 'Price' : 'Prezzo'}
        </p>
        <PriceRangeSlider bounds={priceBounds} value={priceRange} setValue={setPriceRange} language={language} />
      </section>
    )}

    {brandValues.length > 1 && (
      <section className="border-b border-slate-200 py-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
          {language === 'en' ? 'Brand' : 'Marca'}
        </p>
        <div className="space-y-1.5">
          {brandValues.map((brand) => (
            <label key={brand} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={selectedBrands.includes(brand)}
                className="accent-cyan-700"
                onChange={(event) =>
                  setSelectedBrands((current) =>
                    event.target.checked ? [...current, brand] : current.filter((entry) => entry !== brand),
                  )
                }
              />
              <span>{brand}</span>
            </label>
          ))}
        </div>
      </section>
    )}

    {productTypeValues.length > 1 && (
      <section className="border-b border-slate-200 py-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
          {language === 'en' ? 'Product type' : 'Tipologia prodotto'}
        </p>
        <div className="space-y-1.5">
          {productTypeValues.map((type) => (
            <label key={type} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={selectedTypes.includes(type)}
                className="accent-cyan-700"
                onChange={(event) =>
                  setSelectedTypes((current) =>
                    event.target.checked ? [...current, type] : current.filter((entry) => entry !== type),
                  )
                }
              />
              <span>{formatProductTypeLabel(type, language)}</span>
            </label>
          ))}
        </div>
      </section>
    )}

    {numericFacets.map((facet) => (
      <SearchNumericFacetSection
        key={facet.id}
        facet={facet}
        numericFilters={numericFilters}
        setNumericFilters={setNumericFilters}
      />
    ))}

    {optionFacets.map((facet) => (
      <SearchOptionFacetSection
        key={facet.id}
        facet={facet}
        language={language}
        optionFilters={optionFilters}
        setOptionFilters={setOptionFilters}
      />
    ))}
  </div>
)

export default SearchFiltersSidebar
